import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Image as ImageIcon, AlertCircle, CheckCircle } from 'lucide-react';

interface ImageUploadProps {
  category: 'profile' | 'website' | 'complaint_support' | 'general';
  onImageUploaded: (imageUrl: string) => void;
  currentImageUrl?: string;
  disabled?: boolean;
  className?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  category,
  onImageUploaded,
  currentImageUrl,
  disabled = false,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getBaseUrl = () => {
    const envBase = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_PUBLIC_URL || window.location.origin;
    return envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;
  };

  const toAbsoluteUrl = (url: string): string => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    const baseUrl = getBaseUrl();
    if (url.startsWith('/')) {
      return `${baseUrl}${url}`;
    }
    return `${baseUrl}/${url}`;
  };

  const getPreviewCandidates = (url?: string): string[] => {
    if (!url) return [];

    const candidates = new Set<string>();
    const absoluteUrl = toAbsoluteUrl(url);
    const baseUrl = getBaseUrl();

    candidates.add(absoluteUrl);

    // Backward compatibility: some records were stored as /uploads/images/<filename>
    // while newer profile uploads are served from /uploads/images/profile/<filename>
    // or /api/v1/image-upload/public-file/profile/<filename>.
    const fileName = absoluteUrl.split('/').pop();
    if (fileName) {
      candidates.add(`${baseUrl}/uploads/images/profile/${fileName}`);
      candidates.add(`${baseUrl}/api/v1/image-upload/public-file/profile/${fileName}`);
    }

    return Array.from(candidates);
  };

  const previewCandidates = getPreviewCandidates(currentImageUrl);
  const previewSrc = previewCandidates[previewIndex] || '';

  useEffect(() => {
    setPreviewIndex(0);
  }, [currentImageUrl]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }

    await uploadImage(file);
  };

  const uploadImage = async (file: File) => {
    setIsUploading(true);
    setUploadError(null);
    setUploadSuccess(false);

    try {
      const { imageUploadService } = await import('@/services/imageUploadService');
      
      // Validate file first
      const validation = imageUploadService.validateImage(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      const result = await imageUploadService.uploadImage(file, category, true);
      onImageUploaded(result.url);
      setUploadSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveImage = () => {
    onImageUploaded('');
    setUploadError(null);
    setUploadSuccess(false);
    setPreviewIndex(0);
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Current Image Display */}
      {currentImageUrl && (
        <div className="relative inline-block">
          <img
            src={previewSrc}
            alt="Current"
            className="w-24 h-24 object-cover rounded-lg border border-gray-300"
            onError={() => {
              setPreviewIndex((prev) => {
                if (prev + 1 < previewCandidates.length) {
                  return prev + 1;
                }
                return prev;
              });
            }}
          />
          {!disabled && (
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Upload Area */}
      <div
        onClick={handleClick}
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${disabled || isUploading 
            ? 'border-gray-300 bg-gray-50 cursor-not-allowed' 
            : 'border-blue-300 bg-blue-50 hover:border-blue-400 hover:bg-blue-100'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || isUploading}
        />

        {isUploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="w-8 h-8 text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">
              {currentImageUrl ? 'Click to change image' : 'Click to upload image'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PNG, JPG, GIF up to 5MB
            </p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {uploadError && (
        <div className="flex items-center space-x-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{uploadError}</span>
        </div>
      )}

      {/* Success Message */}
      {uploadSuccess && (
        <div className="flex items-center space-x-2 text-green-600 text-sm">
          <CheckCircle className="w-4 h-4" />
          <span>Image uploaded successfully!</span>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
